import { toUserMessage } from '../../lib/errors.js';
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonText,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonRouter,
} from '@ionic/react';
import { add, bluetoothOutline, checkmarkOutline, closeOutline, createOutline, imageOutline, searchOutline, warningOutline } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';

import { scheduleTemperatureReminders } from '../../lib/notifications/temperatureReminder.js';

// T-261: controllo temperature in stile scheda Cambusa — le unità si raggruppano
// in 2 cassetti (Frigoriferi positivi / Congelatori negativi). Abbattitore e
// dispensa non rientrano nel controllo giornaliero (gruppo "Altre").
type CassettoKey = 'frigoriferi' | 'congelatori' | 'altre';
const CASSETTO_META: Record<CassettoKey, { label: string; emoji: string }> = {
  frigoriferi: { label: 'Frigoriferi', emoji: '🧊' },
  congelatori: { label: 'Congelatori', emoji: '❄️' },
  altre: { label: 'Altre unità', emoji: '📦' },
};
function cassettoOfUnit(u: { kind?: string; temp_max_c?: number | null }): CassettoKey {
  const k = u.kind ?? '';
  if (k === 'abbattitore' || k === 'dispensa') return 'altre';
  if (k === 'frigo_negativo') return 'congelatori';
  if (u.temp_max_c != null && u.temp_max_c <= 0) return 'congelatori';
  return 'frigoriferi';
}
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useEnqueueMaster, useEvents, useMasters } from '../../lib/data/queries.js';
import { autoDiscover } from '../../lib/adapters/probes/index.js';
import type { DetectedProbe } from '@trackeat/shared';
import { TemperaturePointEditorModal } from '../../components/editors/TemperaturePointEditorModal.js';
import { EmptyHint, SectionHeader, palette } from '../../components/design.js';
import { FeatureIntroBlock, isIntroDismissed } from '../../components/FeatureCard.js';
import { LateTimeModal } from '../../components/haccp/LateTimeModal.js';
import {
  LATE_ENTRY_LOOKBACK_DAYS,
  combineLocalDateTime,
  formatDayHeading,
  lateEntryMarker,
  localDateKeyOf,
  missingDailyDays,
  type MissingDay,
} from '../../lib/haccp/lateEntry.js';
import { useOperationalIdentity } from '../../lib/auth/useOperationalIdentity.js';
import { isAssignedToOperationalIdentity } from '../../lib/auth/operationalAssignment.js';
import { useOperationalCheckEvent } from '../../lib/haccp/useOperationalCheckEvent.js';
import { evidenceArchiveHref } from '../../lib/haccp/operationalEvidenceArchive.js';

interface StorageUnit {
  id: string;
  name: string;
  kind: string;
  temp_min_c: number | null;
  temp_max_c: number | null;
  business_id: string;
  created_at?: string | null;
  assigned_personnel_ids?: string[] | null;
  requires_photo?: boolean;
}

// Cap di fetch letture: alto per coprire ~30 giorni di controlli; oltre il cap
// non dichiariamo "mancante" un giorno non conoscibile (guard anti-troncamento).
const TEMP_EVENTS_FETCH_CAP = 1000;

interface ProbeRow {
  id: string;
  name: string;
  vendor: string;
  mac_or_id: string | null;
  storage_unit_id: string | null;
  archived_at?: string | null;
  version?: number;
}

interface TemperatureReadingRow {
  payload: {
    storage_unit_id: string;
    temperature_c: number;
    tier: string;
    source: string;
    conformity?: 'ok' | 'ko';
    evidence_photo?: { storage_path?: string };
  };
  client_ts: string;
}

function formatLastCheck(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  if (sameDay) return `oggi ${hh}:${mm}`;
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return `ieri ${hh}:${mm}`;
  return `${d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} ${hh}:${mm}`;
}

type Source = 'manual' | 'probe';

export function TemperaturesPage(): JSX.Element {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const router = useIonRouter();
  const [source, setSource] = useState<Source>('manual');
  // T-333: vista corrente — controlli di oggi vs recupero giorni scorsi.
  const [view, setView] = useState<'oggi' | 'giorni'>('oggi');
  const { data: units = [] } = useMasters<StorageUnit>('storage_units');
  const { data: readings = [] } = useEvents('temperature_reading', TEMP_EVENTS_FETCH_CAP);
  const { data: probesMaster = [] } = useMasters<ProbeRow>('probes');
  const identity = useOperationalIdentity();
  const operationalEvent = useOperationalCheckEvent();
  const createMaster = useEnqueueMaster();
  // T-333: target del recupero postumo (giorno + unità da segnare conformi).
  const [recover, setRecover] = useState<{ dayKey: string; units: StorageUnit[]; label: string } | null>(null);

  const [activeUnit, setActiveUnit] = useState<StorageUnit | null>(null);
  // Modal "Non conforme": chiede temperatura misurata reale prima di registrare.
  const [koUnit, setKoUnit] = useState<StorageUnit | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [introVersion, setIntroVersion] = useState(0);
  const introFeatureKey = 'temperatures';
  const introUserId = profile?.id ?? null;
  const introBusinessId = profile?.active_business_id ?? null;
  void introVersion;
  const [editorUnit, setEditorUnit] = useState<StorageUnit | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [discovered, setDiscovered] = useState<DetectedProbe[] | null>(null);
  const [pairTargetUnit, setPairTargetUnit] = useState<StorageUnit | null>(null);
  // T-228b: target di archiviazione (eliminazione) di un punto di conservazione.
  const [archiveTarget, setArchiveTarget] = useState<{ id: string; name: string; version: number } | null>(null);

  // Archivia (soft-delete) il frigorifero + la sua eventuale sonda. Append-only:
  // archived_at, mai DELETE → lo storico letture resta per audit HACCP, ma il
  // punto sparisce dalla lista e non genera più alert.
  async function archiveUnit(target: { id: string; name: string; version: number }): Promise<void> {
    try {
      await createMaster.mutateAsync({
        table: 'storage_units',
        id: target.id,
        op: 'archive',
        base_version: target.version ?? 0,
        patch: { id: target.id },
      });
      const probe = probesMaster.find((p) => p.storage_unit_id === target.id && !p.archived_at);
      if (probe) {
        await createMaster.mutateAsync({
          table: 'probes',
          id: probe.id,
          op: 'archive',
          base_version: probe.version ?? 0,
          patch: { id: probe.id },
        });
      }
      setToast(`"${target.name}" eliminato`);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setArchiveTarget(null);
    }
  }

  // Ultimo reading per unità: temperatura + timestamp + conformità.
  interface LastReading {
    temp: number;
    ts: string;
    conformity?: 'ok' | 'ko';
    evidencePath?: string;
  }
  const lastByUnit = new Map<string, LastReading>();
  for (const r of readings as TemperatureReadingRow[]) {
    if (!lastByUnit.has(r.payload.storage_unit_id)) {
      lastByUnit.set(r.payload.storage_unit_id, {
        temp: r.payload.temperature_c,
        ts: r.client_ts,
        conformity: r.payload.conformity,
        evidencePath: r.payload.evidence_photo?.storage_path,
      });
    }
  }

  // T-261: 1 controllo/giorno per unità; unità raggruppate nei cassetti.
  const activeUnits = useMemo(
    () => units.filter((u) => !(u as { archived_at?: string | null }).archived_at && isAssignedToOperationalIdentity(
      u.assigned_personnel_ids,
      identity.personnelId,
      identity.canManageOperationalWork,
    )).sort((a, b) => a.name.localeCompare(b.name, 'it', { numeric: true, sensitivity: 'base' })),
    [identity.canManageOperationalWork, identity.personnelId, units],
  );
  const hasFridges = activeUnits.some((u) => cassettoOfUnit(u) !== 'altre');
  // Unità con almeno un controllo OGGI (1 controllo/giorno per unità).
  const checkedTodayIds = useMemo(() => {
    const now = new Date();
    const set = new Set<string>();
    for (const r of readings as TemperatureReadingRow[]) {
      const dt = new Date(r.client_ts);
      if (!Number.isNaN(dt.getTime()) && dt.getFullYear() === now.getFullYear()
        && dt.getMonth() === now.getMonth() && dt.getDate() === now.getDate()) {
        set.add(r.payload.storage_unit_id);
      }
    }
    return set;
  }, [readings]);
  const cassettoGroups = useMemo(() => {
    const g: Record<CassettoKey, StorageUnit[]> = { frigoriferi: [], congelatori: [], altre: [] };
    for (const u of activeUnits) g[cassettoOfUnit(u)].push(u);
    return g;
  }, [activeUnits]);
  useEffect(() => {
    void scheduleTemperatureReminders({ hasFridges });
  }, [hasFridges]);

  // ── T-333: recupero giorni scorsi ──────────────────────────────────────
  // Unità soggette al controllo giornaliero (frigoriferi/congelatori, NON
  // "altre") e con un target impostato (altrimenti non si può segnare conforme).
  const requiredUnits = useMemo(
    () => activeUnits.filter(
      (u) => cassettoOfUnit(u) !== 'altre' && (u.temp_min_c != null || u.temp_max_c != null),
    ),
    [activeUnits],
  );
  // Per ogni unità, l'insieme dei giorni LOCALI con almeno una lettura.
  const doneDayKeysByUnit = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const r of readings as TemperatureReadingRow[]) {
      const dk = localDateKeyOf(r.client_ts);
      if (!dk) continue;
      const set = m.get(r.payload.storage_unit_id) ?? new Set<string>();
      set.add(dk);
      m.set(r.payload.storage_unit_id, set);
    }
    return m;
  }, [readings]);
  const unitCreatedAtMs = useMemo(() => {
    const m = new Map<string, number>();
    for (const u of activeUnits) {
      const ms = u.created_at ? Date.parse(u.created_at) : NaN;
      if (!Number.isNaN(ms)) m.set(u.id, ms);
    }
    return m;
  }, [activeUnits]);
  const earliestTrustedReadingMs = useMemo(() => {
    if (readings.length < TEMP_EVENTS_FETCH_CAP) return null; // dati non troncati
    let earliest = Number.POSITIVE_INFINITY;
    for (const r of readings as TemperatureReadingRow[]) {
      const ms = Date.parse(r.client_ts);
      if (!Number.isNaN(ms) && ms < earliest) earliest = ms;
    }
    return Number.isFinite(earliest) ? earliest : null;
  }, [readings]);
  const missingDays: MissingDay<StorageUnit>[] = useMemo(
    () => missingDailyDays(requiredUnits, doneDayKeysByUnit, {
      todayMs: Date.now(),
      lookbackDays: LATE_ENTRY_LOOKBACK_DAYS,
      createdAtMsById: unitCreatedAtMs,
      earliestTrustedMs: earliestTrustedReadingMs,
    }),
    [requiredUnits, doneDayKeysByUnit, unitCreatedAtMs, earliestTrustedReadingMs],
  );

  function unitMidpoint(unit: StorageUnit): number | null {
    const { temp_min_c: min, temp_max_c: max } = unit;
    if (min != null && max != null) return (min + max) / 2;
    if (min != null) return min;
    if (max != null) return max;
    return null;
  }

  // Recupero postumo: registra controlli conformi retrodatati al giorno scelto,
  // con l'ora dichiarata dall'operatore + marker recorded_late/operational_date.
  async function commitRecover(hhmm: string): Promise<void> {
    if (!recover) return;
    const { dayKey, units: list } = recover;
    let clientTs: string;
    try {
      clientTs = combineLocalDateTime(dayKey, hhmm);
    } catch {
      setToast('Ora non valida');
      return;
    }
    const marker = lateEntryMarker(dayKey, hhmm);
    let done = 0;
    try {
      for (const u of list) {
        const temp = unitMidpoint(u);
        if (temp == null) continue;
        const result = await operationalEvent.record({
          eventKind: 'temperature_reading',
          evidenceKind: 'temperature',
          targetId: u.id,
          businessId: u.business_id,
          requiresPhoto: u.requires_photo === true,
          clientTs,
          payload: {
            storage_unit_id: u.id,
            probe_id: null,
            temperature_c: Number(temp.toFixed(1)),
            tier: 'compliance',
            source: 'manual_check',
            conformity: 'ok',
            ...marker,
          },
        });
        if (result === 'cancelled') break;
        done += 1;
      }
      setToast(
        done === 1
          ? `Recuperato: ${list[0].name} (${formatDayHeading(dayKey)})`
          : `✓ ${done} unità recuperate · ${formatDayHeading(dayKey)}`,
      );
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setRecover(null);
    }
  }

  /** Registra un controllo conforme: temperatura al midpoint del target.
   *  Il timestamp client_ts + actor_user_id + hash chain costituiscono il
   *  certificato HACCP/ASL del controllo effettuato. */
  async function recordCompliant(unit: StorageUnit): Promise<void> {
    const min = unit.temp_min_c;
    const max = unit.temp_max_c;
    let temp: number;
    if (min != null && max != null) temp = (min + max) / 2;
    else if (min != null) temp = min;
    else if (max != null) temp = max;
    else {
      setToast('Imposta prima il target di temperatura');
      return;
    }
    try {
      const result = await operationalEvent.record({
        eventKind: 'temperature_reading',
        evidenceKind: 'temperature',
        targetId: unit.id,
        businessId: unit.business_id,
        requiresPhoto: unit.requires_photo === true,
        payload: {
          storage_unit_id: unit.id,
          probe_id: null,
          temperature_c: Number(temp.toFixed(1)),
          tier: 'compliance',
          source: 'manual_check',
          conformity: 'ok',
        },
      });
      if (result === 'cancelled') return;
      setToast(result === 'recorded_pending_photo'
        ? `Controllo salvato (${unit.name}) · foto in attesa di upload`
        : `✓ Controllo conforme registrato (${unit.name})`);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  // T-261: segna conformi in un colpo solo tutte le unità di un cassetto che non
  // hanno ancora il controllo di oggi (1 controllo/giorno per unità).
  async function recordGroupCompliant(groupUnits: StorageUnit[]): Promise<void> {
    const todo = groupUnits.filter(
      (u) => !checkedTodayIds.has(u.id) && (u.temp_min_c != null || u.temp_max_c != null),
    );
    if (todo.length === 0) { setToast('Già tutti controllati oggi'); return; }
    for (const u of todo) await recordCompliant(u);
    setToast(`✓ ${todo.length} unità segnate conformi`);
  }

  async function recordReading(unit: StorageUnit, raw: string, conformity: 'ok' | 'ko' = 'ok'): Promise<void> {
    const value = Number(raw.replace(',', '.'));
    if (!Number.isFinite(value)) {
      setToast('Valore non valido');
      return;
    }
    const pairedProbe = source === 'probe'
      ? probesMaster.find((p) => p.storage_unit_id === unit.id)
      : null;
    try {
      const result = await operationalEvent.record({
        eventKind: 'temperature_reading',
        evidenceKind: 'temperature',
        targetId: unit.id,
        businessId: unit.business_id,
        requiresPhoto: unit.requires_photo === true,
        payload: {
          storage_unit_id: unit.id,
          probe_id: pairedProbe?.id ?? null,
          temperature_c: value,
          tier: pairedProbe ? 'monitoring' : 'manual',
          source: pairedProbe ? 'ble' : 'manual_staff',
          conformity,
        },
      });
      if (result === 'cancelled') return;
      const conformityLabel = conformity === 'ko' ? '⚠ Non conforme' : 'Registrato';
      setToast(result === 'recorded_pending_photo'
        ? `${conformityLabel}: ${value} °C · foto in attesa di upload`
        : `${conformityLabel}: ${value} °C${pairedProbe ? ` (sonda ${pairedProbe.name})` : ''}`);
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    }
  }

  async function discoverProbes(unit: StorageUnit): Promise<void> {
    // T-219: lo scan BLE delle sonde usa requestLEScan, disponibile solo
    // nell'app nativa. Dal browser (in particolare iPhone/iPad, dove Web
    // Bluetooth non esiste proprio) usciva un errore generico.
    if (!Capacitor.isNativePlatform()) {
      setToast(
        "La ricerca delle sonde Bluetooth è disponibile solo nell'app TrackEAT installata. " +
        'Dal browser — in particolare su iPhone/iPad — il Bluetooth non è accessibile.',
      );
      return;
    }
    setPairTargetUnit(unit);
    setDiscovering(true);
    setDiscovered(null);
    try {
      const list = await autoDiscover(8_000);
      setDiscovered(list);
      if (list.length === 0) setToast('Nessuna sonda BLE rilevata. Avvicina la sonda al telefono.');
    } catch (err) {
      setToast(`Errore BLE: ${toUserMessage(err)}`);
      setDiscovered([]);
    } finally {
      setDiscovering(false);
    }
  }

  async function pairProbe(probe: DetectedProbe): Promise<void> {
    if (!pairTargetUnit || !profile?.active_tenant_id) return;
    const id = crypto.randomUUID();
    try {
      await createMaster.mutateAsync({
        table: 'probes',
        id,
        op: 'insert',
        base_version: 0,
        patch: {
          id,
          tenant_id: profile.active_tenant_id,
          business_id: pairTargetUnit.business_id,
          storage_unit_id: pairTargetUnit.id,
          name: (probe as unknown as { suggested_name?: string }).suggested_name ?? `Sonda ${probe.vendor}`,
          vendor: probe.vendor,
          model: probe.model_hint ?? probe.vendor,
          serial: null,
          mac_or_id: probe.device_id,
          protocol: 'ble',
          tier: 'monitoring',
          calibration_valid_until: null,
          certificate_file_id: null,
          offset_c: 0,
          active: true,
        },
      });
      setToast(`Sonda accoppiata: ${(probe as unknown as { suggested_name?: string }).suggested_name ?? probe.vendor}`);
      setPairTargetUnit(null);
      setDiscovered(null);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>{t('haccp.temperatures')}</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={view} onIonChange={(ev) => setView((ev.detail.value as 'oggi' | 'giorni') ?? 'oggi')}>
            <IonSegmentButton value="oggi">
              <IonLabel>Oggi</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="giorni">
              <IonLabel>Giorni scorsi</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
        {view === 'oggi' && (
          <IonToolbar>
            <IonSegment value={source} onIonChange={(ev) => setSource(ev.detail.value as Source)}>
              <IonSegmentButton value="manual">
                <IonIcon icon={createOutline} />
                <IonLabel>{t('haccp.manual')}</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="probe">
                <IonIcon icon={bluetoothOutline} />
                <IonLabel>{t('haccp.probe')}</IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </IonToolbar>
        )}
      </IonHeader>
      <IonContent style={{ '--background': palette.bg } as React.CSSProperties}>
        {view === 'giorni' ? (
          <div style={{ padding: '8px 16px 96px' }}>
            {missingDays.length === 0 ? (
              <div style={{ background: palette.card, padding: 16, borderRadius: 14, marginTop: 8 }}>
                <EmptyHint>Nessun controllo temperatura arretrato da recuperare 🎉</EmptyHint>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12, color: palette.textFaint, margin: '4px 2px 10px', lineHeight: 1.4 }}>
                  Controlli temperatura non registrati dei giorni scorsi. Tocca
                  un'unità per recuperarla: ti verrà chiesto <b>a che ora</b> hai
                  fatto il controllo, così il registro resta veritiero.
                </div>
                {missingDays.map((day) => (
                  <div
                    key={day.date}
                    style={{
                      background: palette.card,
                      borderRadius: 16,
                      boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 8px rgba(15,23,42,.06)',
                      margin: '12px 0',
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 14px 8px' }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: palette.text, textTransform: 'capitalize' }}>
                        {formatDayHeading(day.date)}
                      </span>
                      <span style={{ fontSize: 12, color: palette.textFaint }}>{day.items.length} da recuperare</span>
                    </div>
                    <div style={{ padding: '0 14px' }}>
                      {day.items.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => setRecover({ dayKey: day.date, units: [u], label: u.name })}
                          style={{
                            width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                            borderBottom: `1px solid ${palette.border}`, padding: '11px 0',
                            display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                          }}
                        >
                          <span style={{ flex: 1, fontSize: 14, color: palette.text }}>{u.name}</span>
                          <span style={{ fontSize: 12, color: palette.textFaint }}>
                            {u.temp_min_c ?? '?'}° / {u.temp_max_c ?? '?'}°
                          </span>
                          <IonIcon icon={checkmarkOutline} style={{ fontSize: 18, color: palette.success }} />
                        </button>
                      ))}
                    </div>
                    {day.items.length > 1 && (
                      <div style={{ padding: '10px 14px 14px' }}>
                        <button
                          type="button"
                          onClick={() => setRecover({ dayKey: day.date, units: day.items, label: `${day.items.length} unità` })}
                          style={{
                            width: '100%', background: palette.success, color: '#fff', border: 'none',
                            borderRadius: 10, padding: '11px 8px', fontSize: 13, fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer',
                          }}
                        >
                          <IonIcon icon={checkmarkOutline} /> Tutte conformi ({day.items.length})
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        ) : (
        <div style={{ padding: '8px 16px 96px' }}>
          {/* T-261: 1 controllo/giorno per unità (modello scheda Cambusa). */}
          {hasFridges && (() => {
            const dailyUnits = [...cassettoGroups.frigoriferi, ...cassettoGroups.congelatori];
            const done = dailyUnits.filter((u) => checkedTodayIds.has(u.id)).length;
            const allDone = dailyUnits.length > 0 && done === dailyUnits.length;
            return (
              <div style={{
                background: allDone ? '#dcfce7' : '#fef3c7',
                border: `1px solid ${allDone ? '#86efac' : '#fcd34d'}`,
                borderRadius: 12, padding: '10px 14px', marginBottom: 12,
              }}>
                <b style={{ fontSize: 14, color: '#0f172a' }}>Controllo temperature di oggi: {done}/{dailyUnits.length}</b>
                <div style={{ fontSize: 12, color: '#92400e', marginTop: 4 }}>
                  {allDone
                    ? '✓ Tutte le unità controllate oggi.'
                    : 'Basta 1 controllo al giorno per ogni frigorifero e congelatore. Usa "Segna tutti conformi" o il tasto Conforme su ogni unità.'}
                </div>
              </div>
            );
          })()}
          <SectionHeader title={t('haccp.unitName')} />
          {units.length === 0 && !isIntroDismissed(introFeatureKey, introUserId, introBusinessId) ? (
            <FeatureIntroBlock
              featureKey={introFeatureKey}
              icon="🌡️"
              title="Registro temperature HACCP"
              description={
                <>
                  Tenere sotto controllo le temperature di frigoriferi,
                  congelatori e vetrine è uno degli <b>obblighi cardine
                  del Manuale HACCP</b>. Qui anagrafi ogni "punto" di
                  conservazione (es. "Frigo cucina", "Banco bar",
                  "Pozzetto gelato") con la sua soglia min/max.
                  <br /><br />
                  Una volta configurati, puoi registrare le rilevazioni
                  manuali — oppure collegare una sonda Bluetooth per la
                  raccolta automatica. Ogni lettura fuori soglia genera
                  un'allerta in dashboard, e tutto il registro è
                  esportabile per ispezioni o controlli ASL.
                </>
              }
              whatsappQuestion="Ciao, ho dubbi sul registro temperature HACCP di TrackEAT, mi puoi aiutare?"
              startLabel="Aggiungi il primo frigo"
              onStart={() => { setIntroVersion((v) => v + 1); setEditorUnit(null); setEditorOpen(true); }}
              userId={introUserId}
              businessId={introBusinessId}
              flush
            />
          ) : units.length === 0 ? (
            <div style={{ background: palette.card, padding: 16, borderRadius: 14, marginTop: 8 }}>
              <EmptyHint>{t('common.empty')}</EmptyHint>
            </div>
          ) : (
            (['frigoriferi', 'congelatori', 'altre'] as CassettoKey[]).map((ck) => {
              const groupUnits = cassettoGroups[ck];
              if (groupUnits.length === 0) return null;
              const meta = CASSETTO_META[ck];
              const checkedN = groupUnits.filter((gu) => checkedTodayIds.has(gu.id)).length;
              return (
                <div key={ck}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, margin: '14px 4px 2px' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: palette.text }}>{meta.emoji} {meta.label}</span>
                    <span style={{ fontSize: 12, color: checkedN === groupUnits.length ? palette.success : palette.textFaint }}>{checkedN}/{groupUnits.length} oggi</span>
                  </div>
                  {ck !== 'altre' && checkedN < groupUnits.length && (
                    <button type="button" onClick={() => void recordGroupCompliant(groupUnits)}
                      style={{ width: '100%', background: palette.success, color: '#fff', border: 'none', borderRadius: 10, padding: '9px', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginTop: 4 }}>
                      ✓ Segna tutti conformi oggi
                    </button>
                  )}
                  {groupUnits.map((u) => {
              const last = lastByUnit.get(u.id);
              const targetMin = u.temp_min_c;
              const targetMax = u.temp_max_c;
              const target = `${targetMin ?? '?'}° / ${targetMax ?? '?'}°`;
              const paired = probesMaster.find((p) => p.storage_unit_id === u.id);
              const isKo = last?.conformity === 'ko';
              const lastIsToday = !!last && localDateKeyOf(last.ts) === localDateKeyOf(new Date().toISOString());
              const accentColor = last === undefined
                ? palette.textFaint
                : isKo
                ? palette.danger
                : palette.success;
              return (
                <div
                  key={u.id}
                  style={{
                    background: palette.card,
                    borderRadius: 14,
                    borderLeft: `4px solid ${accentColor}`,
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                    margin: '10px 0',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header (cliccabile per modificare il frigo). */}
                  <div
                    onClick={identity.canManageOperationalWork ? () => { setEditorUnit(u); setEditorOpen(true); } : undefined}
                    role={identity.canManageOperationalWork ? 'button' : undefined}
                    style={{
                      padding: '14px 16px',
                      display: 'flex', alignItems: 'center', gap: 12,
                      cursor: identity.canManageOperationalWork ? 'pointer' : 'default',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 15, fontWeight: 600, color: palette.text }}>{u.name}</span>
                        {paired && (
                          <IonIcon icon={bluetoothOutline} style={{ fontSize: 14, color: palette.accent, flexShrink: 0 }} />
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: palette.textFaint }}>
                        Target {target}
                        {paired && ` · sonda ${paired.name}`}
                        {u.assigned_personnel_ids?.length ? ` · ${u.assigned_personnel_ids.length} assegnat${u.assigned_personnel_ids.length === 1 ? 'o' : 'i'}` : ''}
                        {u.requires_photo ? ' · 📸 foto' : ''}
                      </div>
                      {last && (
                        <div style={{ fontSize: 11, color: isKo ? palette.danger : palette.textFaint, marginTop: 4 }}>
                          {isKo ? '⚠ ' : '✓ '}
                          Ultimo controllo {formatLastCheck(last.ts)}
                          {isKo && ` · ${last.temp.toFixed(1)}°`}
                        </div>
                      )}
                    </div>
                    {!paired && (
                      <IonButton
                        fill="clear"
                        size="small"
                        onClick={(e) => { e.stopPropagation(); void discoverProbes(u); }}
                        aria-label="Cerca sonda BLE"
                      >
                        <IonIcon icon={searchOutline} slot="icon-only" />
                      </IonButton>
                    )}
                  </div>
                  {lastIsToday && identity.canManageOperationalWork && last?.evidencePath && (
                    <div style={{ padding: '0 12px 6px' }}>
                      <IonButton
                        fill="clear"
                        size="small"
                        aria-label={`Vedi foto del controllo odierno di ${u.name}`}
                        onClick={() => router.push(evidenceArchiveHref({ kind: 'temperature', targetId: u.id, clientTs: last.ts }), 'forward')}
                        style={{ minHeight: 44, margin: 0 }}
                      >
                        <IonIcon icon={imageOutline} slot="start" /> Vedi foto
                      </IonButton>
                    </div>
                  )}
                  {lastIsToday && u.requires_photo && !last?.evidencePath && (
                    <div role="status" style={{ padding: '0 16px 10px', color: '#9a3412', fontSize: 12, fontWeight: 650 }}>
                      Foto non collegata · ripeti il controllo per acquisirla
                    </div>
                  )}
                  {/* Bottoni controllo HACCP: Conforme / Non conforme. La pressione
                      su Conforme registra un evento temperature_reading (tier
                      compliance) col timestamp, costituendo il certificato HACCP
                      del controllo effettuato. Non conforme apre input per la
                      temperatura misurata reale. */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr',
                    borderTop: '1px solid var(--ion-color-light-shade, #e5e7eb)',
                  }}>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); void recordCompliant(u); }}
                      style={{
                        background: 'transparent', border: 'none',
                        padding: '10px 8px', fontSize: 13, fontWeight: 600,
                        color: palette.success, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                      }}
                    >
                      <IonIcon icon={checkmarkOutline} style={{ fontSize: 16 }} />
                      Conforme
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setKoUnit(u); }}
                      style={{
                        background: 'transparent', border: 'none',
                        borderLeft: '1px solid var(--ion-color-light-shade, #e5e7eb)',
                        padding: '10px 8px', fontSize: 13, fontWeight: 600,
                        color: palette.danger, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                      }}
                    >
                      <IonIcon icon={warningOutline} style={{ fontSize: 16 }} />
                      Non conforme
                    </button>
                  </div>
                </div>
              );
                  })}
                </div>
              );
            })
          )}
        </div>
        )}

        {view === 'oggi' && identity.canManageOperationalWork && (
          <IonFab slot="fixed" vertical="bottom" horizontal="end">
            <IonFabButton aria-label={t('haccp.addUnit')} onClick={() => { setEditorUnit(null); setEditorOpen(true); }}>
              <IonIcon icon={add} />
            </IonFabButton>
          </IonFab>
        )}

        <LateTimeModal
          isOpen={recover !== null}
          title="Recupero controllo"
          dayKey={recover?.dayKey ?? null}
          subtitle={recover?.label}
          onConfirm={(hhmm) => void commitRecover(hhmm)}
          onDismiss={() => setRecover(null)}
        />

        <TemperaturePointEditorModal
          isOpen={editorOpen}
          initial={editorUnit ?? undefined}
          onDismiss={() => { setEditorOpen(false); setEditorUnit(null); }}
          onSaved={() => setToast(editorUnit ? 'Frigorifero aggiornato' : 'Frigorifero aggiunto')}
          onRequestArchive={(rec) => { setEditorOpen(false); setEditorUnit(null); setArchiveTarget(rec); }}
        />

        {/* T-228b: conferma eliminazione frigorifero (archiviazione soft) */}
        <IonAlert
          isOpen={!!archiveTarget}
          header="Elimina frigorifero"
          message={archiveTarget ? `Eliminare "${archiveTarget.name}"? Lo storico delle letture resta archiviato per i registri HACCP, ma il punto non comparirà più nella lista.` : ''}
          buttons={[
            { text: 'Annulla', role: 'cancel', handler: () => setArchiveTarget(null) },
            { text: 'Elimina', role: 'destructive', handler: () => { if (archiveTarget) void archiveUnit(archiveTarget); } },
          ]}
          onDidDismiss={() => setArchiveTarget(null)}
        />

        <IonModal isOpen={!!pairTargetUnit} onDidDismiss={() => { setPairTargetUnit(null); setDiscovered(null); }}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Cerca sonda BLE</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => { setPairTargetUnit(null); setDiscovered(null); }} aria-label="chiudi">
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonText color="medium">
              <p style={{ fontSize: 13 }}>
                Sonde supportate: Inkbird, Ruuvi, Govee. Avvicina la sonda al telefono e attendi
                la scansione (8 secondi). Il dispositivo verrà accoppiato a <b>{pairTargetUnit?.name}</b>.
              </p>
            </IonText>
            {discovering && (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <IonIcon icon={bluetoothOutline} style={{ fontSize: 48, color: palette.accent }} />
                <p style={{ fontSize: 13, marginTop: 12 }}>Scansione in corso...</p>
              </div>
            )}
            {!discovering && discovered && (
              <IonList inset>
                <IonListHeader><IonLabel>{discovered.length} sonde rilevate</IonLabel></IonListHeader>
                {discovered.length === 0 ? (
                  <IonItem><IonLabel color="medium">Nessuna sonda trovata. Verifica permessi BLE e prossimità.</IonLabel></IonItem>
                ) : (
                  discovered.map((p) => (
                    <IonItem key={p.device_id} button onClick={() => { void pairProbe(p); }}>
                      <IonIcon icon={bluetoothOutline} slot="start" color="primary" />
                      <IonLabel>
                        <h3>{p.name || p.vendor}</h3>
                        <p style={{ fontSize: 11 }}>
                          {p.vendor} · ID {p.device_id.slice(0, 18)}
                          {p.rssi != null ? ` · RSSI ${p.rssi}` : ''}
                          {p.model_hint ? ` · ${p.model_hint}` : ''}
                        </p>
                      </IonLabel>
                    </IonItem>
                  ))
                )}
                <div style={{ padding: '12px 16px' }}>
                  <IonButton expand="block" fill="outline" onClick={() => pairTargetUnit && void discoverProbes(pairTargetUnit)}>
                    Cerca di nuovo
                  </IonButton>
                </div>
              </IonList>
            )}
          </IonContent>
        </IonModal>

        {/* Alert "Non conforme": chiede la temperatura misurata reale che ha
            generato la non-conformità. Si registra come temperature_reading
            con conformity='ko' → produce log audit ASL della rilevazione fuori
            range. */}
        <IonAlert
          isOpen={!!koUnit}
          header={koUnit ? `Non conforme — ${koUnit.name}` : ''}
          message="Inserisci la temperatura rilevata che ha causato la non-conformità."
          inputs={[
            {
              name: 'temp',
              type: 'number',
              placeholder: '°C misurati',
              attributes: { inputmode: 'decimal', step: '0.1' },
            },
          ]}
          buttons={[
            { text: t('common.cancel'), role: 'cancel' },
            {
              text: 'Registra non-conformità',
              handler: (data: { temp?: string }) => {
                if (koUnit && data.temp) void recordReading(koUnit, data.temp, 'ko');
                setKoUnit(null);
              },
            },
          ]}
          onDidDismiss={() => setKoUnit(null)}
        />
        {/* Legacy: alert temperatura libera. Manteniamo per compatibilità con
            input manuale arbitrario via `setActiveUnit`. */}
        <IonAlert
          isOpen={!!activeUnit}
          header={activeUnit?.name ?? ''}
          message={t('haccp.newReading')}
          inputs={[
            {
              name: 'temp',
              type: 'number',
              placeholder: '°C',
              attributes: { inputmode: 'decimal', step: '0.1' },
            },
          ]}
          buttons={[
            { text: t('common.cancel'), role: 'cancel' },
            {
              text: t('common.save'),
              handler: (data: { temp?: string }) => {
                if (activeUnit && data.temp) void recordReading(activeUnit, data.temp);
                setActiveUnit(null);
              },
            },
          ]}
          onDidDismiss={() => setActiveUnit(null)}
        />

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={1800}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}
